import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledGroupAndUserMultiSelect from 'src/components/form/controlled-group-and-user-multi-select';
import ControlledInput from 'src/components/form/controlled-input';
import type { StatusType } from 'src/components/form/controlled-select/ControlledSelect';
import ControlledTextarea from 'src/components/form/controlled-textarea';

import { useGetSchedulableTargets } from '@/hooks/queries/scheduler/useGetSchedulableTargets';
import { useGetScheduleTaskTypes } from '@/hooks/queries/scheduler/useGetScheduleTaskTypes';

import { AssignmentField } from './AssignmentField';
import type { ScheduleFormFieldData } from './scheduleFormSchema';
import { toTargetOptions } from './scheduleTargets';
import { TargetField } from './TargetField';
import type { TaskTypeOption } from './TaskTypeField';
import { TaskTypeField } from './TaskTypeField';

export const ScheduleDetailsFields: FC = () => {
  const { t } = useTranslation(['common']);
  const { control } = useFormContext<ScheduleFormFieldData>();
  const taskType = useWatch({ control, name: 'TaskType' });

  // The same read the "Add schedule" button gates on, so the button and this
  // picker cannot disagree: a button that opens a form with an empty dropdown is
  // the failure that pairing prevents.
  const {
    data: taskTypeRows,
    isPending: taskTypesPending,
    isError: taskTypesFailed,
  } = useGetScheduleTaskTypes();
  const {
    data: schedulableTargets,
    isPending: targetsPending,
    isError: targetsFailed,
  } = useGetSchedulableTargets(taskType === '' ? [] : [taskType]);

  // A failed read leaves the options empty, which a 'finished' picker renders as
  // "no matches" — indistinguishable from an org that owns no controls.
  const toStatusType = (pending: boolean, failed: boolean): StatusType => {
    if (pending) {
      return 'loading';
    }

    return failed ? 'error' : 'finished';
  };

  const taskTypeOptions = useMemo<TaskTypeOption[]>(
    () =>
      (taskTypeRows ?? []).map((row) => ({
        value: row.Value,
        label: row.Label,
        // Client-side and keyed on the type's value, so a newly seeded type
        // renders label-only until someone adds the string.
        description:
          t(`scheduler.task_type_descriptions.${row.Value}`, {
            defaultValue: '',
          }) || undefined,
      })),
    [taskTypeRows, t]
  );

  const selectedTaskType = useMemo(
    () => (taskTypeRows ?? []).find((row) => row.Value === taskType),
    [taskTypeRows, taskType]
  );

  const targetOptions = useMemo(
    () =>
      selectedTaskType
        ? toTargetOptions(
            schedulableTargets ?? [],
            selectedTaskType.Value,
            selectedTaskType.TargetType
          )
        : [],
    [selectedTaskType, schedulableTargets]
  );

  return (
    <>
      <ControlledInput
        control={control}
        name={'Name'}
        label={t('scheduler.fields.title')}
        testId={'schedule-name'}
      />

      <ControlledTextarea
        control={control}
        name={'Description'}
        label={t('scheduler.fields.description')}
        testId={'schedule-description'}
      />

      {/*
        Task type first: it decides which objects the picker below can offer,
        because each type targets exactly one object type.
      */}
      <TaskTypeField
        options={taskTypeOptions}
        statusType={toStatusType(taskTypesPending, taskTypesFailed)}
      />

      <TargetField
        options={targetOptions}
        statusType={toStatusType(targetsPending, targetsFailed)}
      />

      <AssignmentField />

      <ControlledGroupAndUserMultiSelect
        control={control}
        name={'Owners'}
        label={t('scheduler.fields.owners')}
        includeGroups={true}
        testId={'schedule-owners'}
      />
    </>
  );
};
